import { CollectionViewer } from '@angular/cdk/collections';
import {
  AgrDataSource,
  PaginatedDataSource,
  PaginationModel,
  SortableDataSource,
  Sorting,
} from '@next/core-lib/table-v2';
import { Actions, ofType } from '@ngrx/effects';
import { Store } from '@ngrx/store';
import {
  BehaviorSubject,
  Observable,
  Subject,
  combineLatest,
  debounceTime,
  map,
  shareReplay,
  takeUntil,
  tap,
  withLatestFrom,
} from 'rxjs';
import {
  OfferOverviewModel,
  OrderOptions,
} from 'src/app/dashboard/components/overview/models/overview.model';

import { PageAndSize } from 'src/app/dashboard/components/overview/models/paginated-overview.model';

import * as MyAdvertisementsActions from '../../store/actions/my-advertisements.actions';
import * as MyAdvertisementsSelector from '../../store/selectors/my-advertisements.selector';

/* eslint-disable brace-style */
export class AdvertisementsActiveOffersDataSource
  extends AgrDataSource<OfferOverviewModel>
  implements PaginatedDataSource<OfferOverviewModel>, SortableDataSource<OfferOverviewModel>
{
  public data$ = new BehaviorSubject<OfferOverviewModel[]>([]);
  activeOffers$ = this.store.select(MyAdvertisementsSelector.selectActiveOfferList);
  readonly _sortingSubject$ = new BehaviorSubject<Sorting>({});

  readonly order$: Observable<OrderOptions> = this._sortingSubject$.pipe(
    map((sorting) => {
      const sortColumn = Object.keys(sorting)[0];
      const sortDirection = sorting[sortColumn];

      return sortColumn && sortDirection
        ? { column: sortColumn, descending: sortDirection === 'desc' }
        : undefined;
    }),
  );

  private pageAndSizeSubject$ = new BehaviorSubject<PageAndSize>({
    pageIndex: 0,
    pageSize: 25,
  });

  private readonly destroyPaginatorSubject$ = new Subject<void>();
  private readonly destroySubject$ = new Subject<boolean>();

  constructor(
    private readonly store: Store,
    private readonly actions$: Actions,
  ) {
    super();

    combineLatest([this.pageAndSizeSubject$])
      .pipe(
        debounceTime(100),
        tap(() => this.loadingSubject.next(true)),
        takeUntil(this.destroySubject$),
      )
      .subscribe(([paginationOptions]) => {
        this.store.dispatch(MyAdvertisementsActions.loadActiveOffers({ paginationOptions }));
      });

    this.actions$
      .pipe(
        ofType(MyAdvertisementsActions.deleteOfferSuccess),
        withLatestFrom(this.pageAndSizeSubject$),
        takeUntil(this.destroySubject$),
        tap(([_, pageAndSize]) => {
          this.pageAndSizeSubject$.next(pageAndSize);
        }),
      )
      .subscribe();
  }

  connect(_: CollectionViewer): Observable<OfferOverviewModel[]> {
    combineLatest([this.activeOffers$, this.order$])
      .pipe(
        debounceTime(100),
        tap(() => this.loadingSubject.next(true)),
        takeUntil(this.destroySubject$),
        shareReplay(1),
      )
      .subscribe(([offers, order]) => {
        let sortedOffers = offers;

        if (order && order.column) {
          sortedOffers = this.sortOffers(offers, order.column, order.descending);
        }

        this.loadingSubject.next(false);
        this.data$.next(sortedOffers);
      });
    return this.data$;
  }

  disconnect(collectionViewer: CollectionViewer): void {
    super.disconnect(collectionViewer);
    this.destroySubject$.next(true);
    this.destroySubject$.complete();
  }

  connectPaginator(): Observable<PaginationModel> {
    return this.pageAndSizeSubject$.pipe(
      takeUntil(this.destroyPaginatorSubject$),
      map(({ pageIndex, pageSize }) => ({
        pageSize,
        pageIndex,
        length: this.data$.value.length,
      })),
    );
  }

  disconnectPaginator(): void {
    if (!this.destroyPaginatorSubject$.isStopped) {
      this.destroyPaginatorSubject$.next();
      this.destroyPaginatorSubject$.complete();
    }
  }

  applySorting(sorting: Sorting): void {
    this._sortingSubject$.next(sorting);
  }

  applyPage(pageIndex: number, pageSize: number): void {
    this.pageAndSizeSubject$.next({ pageIndex, pageSize });
  }

  sortOffers(
    activeOffers: OfferOverviewModel[],
    clickedColumn: string,
    descending: boolean,
  ): OfferOverviewModel[] {
    const sortedActiveOffers = [...activeOffers];
    sortedActiveOffers.sort((a, b) => {
      const property = clickedColumn as keyof OfferOverviewModel;
      const valueA = a[property];
      const valueB = b[property];

      if (typeof valueA === 'string' && typeof valueB === 'string') {
        return descending ? valueB.localeCompare(valueA) : valueA.localeCompare(valueB);
      }
      if (typeof valueA === 'number' && typeof valueB === 'number') {
        return descending ? valueB - valueA : valueA - valueB;
      }
      if (typeof valueA === 'boolean' && typeof valueB === 'boolean') {
        return descending ? (valueA ? 1 : -1) : valueA ? -1 : 1;
      }
      return 0;
    });
    return sortedActiveOffers;
  }
}
